import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import './circular-gallery.css';

export interface WeddingGalleryItem {
  imageUrl: string;
  title?: string;
  subtitle?: string;
  isVideo?: boolean;
}

interface CircularGalleryProps {
  items: WeddingGalleryItem[];
  /** Auto-rotate the ring when idle. Disabled automatically under prefers-reduced-motion. */
  autoRotate?: boolean;
  className?: string;
  /** Called when the already-focused (front-facing) card is clicked again — e.g. to open a lightbox. */
  onItemActivate?: (item: WeddingGalleryItem, index: number) => void;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>('desktop');
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setBp(w < 640 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop');
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return bp;
}

const RADIUS_BY_BREAKPOINT: Record<Breakpoint, number> = {
  desktop: 420,
  tablet: 280,
  mobile: 180,
};

const CARD_WIDTH_BY_BREAKPOINT: Record<Breakpoint, number> = {
  desktop: 220,
  tablet: 170,
  mobile: 130,
};

const VISIBLE_ARC_BY_BREAKPOINT: Record<Breakpoint, number> = {
  desktop: 360,
  tablet: 360,
  mobile: 220,
};

interface GalleryCardProps {
  item: WeddingGalleryItem;
  angle: number;
  radius: number;
  isFocused: boolean;
  inVisibleArc: boolean;
  onFocus: () => void;
}

const GalleryCard = memo<GalleryCardProps>(function GalleryCard({
  item, angle, radius, isFocused, inVisibleArc, onFocus,
}) {
  const style: React.CSSProperties = {
    transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
    opacity: inVisibleArc ? 1 : 0,
    pointerEvents: inVisibleArc ? 'auto' : 'none',
  };

  return (
    <button
      type="button"
      className={`wcg-card${isFocused ? ' wcg-card-focused' : ''}`}
      style={style}
      onClick={onFocus}
      aria-label={item.title || 'Gallery photo'}
      aria-current={isFocused}
    >
      {inVisibleArc && (
        <img
          src={item.imageUrl}
          alt={item.title || 'Wedding gallery photo'}
          className="wcg-card-img"
          loading="lazy"
          decoding="async"
        />
      )}
      {item.isVideo && <span className="wcg-card-video-badge" aria-hidden="true">▶</span>}
      {(item.title || item.subtitle) && (
        <div className="wcg-card-caption">
          {item.title && <p className="wcg-card-title">{item.title}</p>}
          {item.subtitle && <p className="wcg-card-subtitle">{item.subtitle}</p>}
        </div>
      )}
    </button>
  );
});

export const CircularGallery: React.FC<CircularGalleryProps> = ({
  items,
  autoRotate = true,
  className = '',
  onItemActivate,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const breakpoint = useBreakpoint();
  const visibleArc = VISIBLE_ARC_BY_BREAKPOINT[breakpoint];

  // Cap the ring radius to the breakpoint max, but for small item counts shrink it
  // so adjacent cards form a tight ring instead of leaving a large empty center gap.
  const radius = useMemo(() => {
    const maxRadius = RADIUS_BY_BREAKPOINT[breakpoint];
    const cardWidth = CARD_WIDTH_BY_BREAKPOINT[breakpoint];
    const n = Math.max(items.length, 3);
    const idealRadius = (cardWidth / 2) / Math.tan(Math.PI / n);
    return Math.min(maxRadius, Math.max(cardWidth * 0.55, idealRadius));
  }, [breakpoint, items.length]);

  const [rotation, setRotation] = useState(0);
  const [focused, setFocused] = useState(0);
  const dragState = useRef<{ startX: number; startRotation: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const pausedRef = useRef(false);

  const step = useMemo(() => (items.length > 0 ? 360 / items.length : 0), [items.length]);

  const shouldAutoRotate = autoRotate && !reducedMotion && items.length > 1;

  useEffect(() => {
    if (!shouldAutoRotate) return;
    let last = performance.now();
    const speed = breakpoint === 'mobile' ? 6 : 10; // degrees per second
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!pausedRef.current) {
        setRotation((r) => r + speed * dt);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [shouldAutoRotate, breakpoint]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    pausedRef.current = true;
    dragState.current = { startX: e.clientX, startRotation: rotation };
  }, [rotation]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    setRotation(dragState.current.startRotation + dx * 0.3);
  }, []);

  const [snapping, setSnapping] = useState(false);
  const snapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bringToFront = useCallback((angle: number) => {
    pausedRef.current = true;
    setSnapping(true);
    setRotation((r) => {
      const target = -angle;
      const diff = ((target - r + 540) % 360) - 180; // shortest signed delta, normalized to [-180, 180)
      return r + diff;
    });
    if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
    snapTimeoutRef.current = setTimeout(() => {
      setSnapping(false);
      pausedRef.current = false;
    }, 450);
  }, []);

  useEffect(() => () => {
    if (snapTimeoutRef.current) clearTimeout(snapTimeoutRef.current);
  }, []);

  const onPointerUp = useCallback(() => {
    dragState.current = null;
    pausedRef.current = false;
  }, []);

  const itemsWithAngle = useMemo(
    () => items.map((item, i) => ({ item, angle: i * step })),
    [items, step],
  );

  const isInVisibleArc = useCallback(
    (angle: number) => {
      const normalized = ((angle + rotation) % 360 + 360) % 360;
      const distFromFront = Math.min(normalized, 360 - normalized);
      return distFromFront <= visibleArc / 2;
    },
    [rotation, visibleArc],
  );

  if (items.length === 0) return null;

  return (
    <div
      className={`wcg-root ${className}`}
      role="region"
      aria-label="Couple photo gallery"
    >
      <div
        className="wcg-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <div
          className={`wcg-ring${snapping ? ' wcg-ring-snapping' : ''}`}
          style={{ transform: `translateZ(-${radius}px) rotateY(${-rotation}deg)` }}
        >
          {itemsWithAngle.map(({ item, angle }, i) => (
            <GalleryCard
              key={`${item.imageUrl}-${i}`}
              item={item}
              angle={angle}
              radius={radius}
              isFocused={i === focused}
              inVisibleArc={isInVisibleArc(angle)}
              onFocus={() => {
                if (i === focused) onItemActivate?.(item, i);
                else {
                  setFocused(i);
                  bringToFront(angle);
                }
              }}
            />
          ))}
        </div>
      </div>
      <p className="wcg-hint" aria-hidden="true">← drag to explore the gallery →</p>
    </div>
  );
};
